import React, { useState } from 'react';
import api from '../../services/api';
import './Admin.css';

const WeatherManager = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const fetchWeather = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await api.get('/weather/fetch');
      
      if (response.data.success) {
        setWeather(response.data.weather);
        setMessage(`Weergegevens opgehaald${response.data.isDemo ? ' (DEMO MODE)' : ''}`);
        setMessageType('success');
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Fout bij ophalen weergegevens');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const broadcastWeather = async () => {
    if (!weather) {
      setMessage('Haal eerst de weergegevens op');
      setMessageType('error');
      return;
    }

    setBroadcasting(true);
    setMessage('');

    try {
      const response = await api.post('/weather/broadcast', {
        weatherData: weather
      });

      if (response.data.success) {
        setMessage(`Weer in queue! Wordt afgespeeld na het huidige nummer. (Positie: ${response.data.queuePosition})`);
        setMessageType('success');
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Fout bij uitzenden weer');
      setMessageType('error');
    } finally {
      setBroadcasting(false);
    }
  };

  const getWeatherIcon = (description) => {
    if (!description) return 'fa-cloud';
    
    const desc = description.toLowerCase();
    if (desc.includes('zon') || desc.includes('helder')) return 'fa-sun';
    if (desc.includes('regen')) return 'fa-cloud-rain';
    if (desc.includes('onweer') || desc.includes('bui')) return 'fa-cloud-bolt';
    if (desc.includes('sneeuw')) return 'fa-snowflake';
    if (desc.includes('bewolkt')) return 'fa-cloud';
    if (desc.includes('mist') || desc.includes('nevel')) return 'fa-smog';
    return 'fa-cloud';
  };

  const getRegionName = (region) => {
    const names = {
      north: 'Noord Nederland',
      middle: 'Midden Nederland',
      south: 'Zuid Nederland'
    };
    return names[region] || region;
  };

  return (
    <div className="weather-manager-page">
      <div className="page-header">
        <h1><i className="fas fa-cloud-sun"></i> Weer Uitzenden</h1>
        <p className="page-subtitle">Haal actuele weersverwachting op voor Nederland en stuur het live de radio op</p>
      </div>

      <div className="weather-actions">
        <button 
          onClick={fetchWeather} 
          className="btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Weer ophalen...
            </>
          ) : (
            <>
              <i className="fas fa-sync"></i> Haal Actueel Weer Op
            </>
          )}
        </button>

        {weather && (
          <button 
            onClick={broadcastWeather} 
            className="btn-success"
            disabled={broadcasting}
          >
            {broadcasting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Aan het uitzenden...
              </>
            ) : (
              <>
                <i className="fas fa-broadcast-tower"></i> Uitzenden
              </>
            )}
          </button>
        )}
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          <i className={`fas fa-${messageType === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message}
        </div>
      )}

      {weather && (
        <div className="weather-grid">
          {Object.entries(weather).map(([region, data]) => (
            <div key={region} className="weather-card">
              <div className="weather-card-header">
                <h3>{getRegionName(region)}</h3>
                {data.city && <span className="weather-city">{data.city}</span>}
              </div>
              
              <div className="weather-main">
                <i className={`fas ${getWeatherIcon(data.description)} weather-icon`}></i>
                <div className="weather-temp">{data.temp}°C</div>
              </div>
              
              <div className="weather-details">
                <div className="weather-detail-item">
                  <i className="fas fa-cloud"></i>
                  <span>{data.description}</span>
                </div>
                <div className="weather-detail-item">
                  <i className="fas fa-wind"></i>
                  <span>{data.wind} km/u</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="info-box">
        <h3><i className="fas fa-info-circle"></i> Hoe werkt het?</h3>
        <ul>
          <li><strong>Stap 1:</strong> Klik op "Haal Actueel Weer Op" om de weersverwachting te laden</li>
          <li><strong>Stap 2:</strong> Bekijk het weer voor Noord, Midden en Zuid Nederland</li>
          <li><strong>Stap 3:</strong> Klik op "Uitzenden" om de AI een weerbericht te laten genereren</li>
          <li><strong>Timing:</strong> Het weer wordt via TTS voorgelezen na het huidige nummer</li>
          <li><strong>Data Bron:</strong> Open-Meteo API (100% GRATIS, geen API key nodig)</li>
          <li><strong>Taal:</strong> Alle weersbeschrijvingen zijn in het Nederlands</li>
          <li><strong>Info:</strong> Open-Meteo is volledig gratis en heeft geen limieten</li>
        </ul>
      </div>
    </div>
  );
};

export default WeatherManager;
